import { useCallback, useEffect, useState, useMemo } from 'react';
// material
import {
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Box, styled } from '@mui/system';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// components
import { useDispatch } from 'react-redux';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserMoreMenu } from '../../components/_dashboard/user';
import UserListToolbar from '../../components/_dashboard/user/UserListToolbar';
import { fDateNew } from '../../utils/formatTime';
import useUserSelector from '../../store/Slice/User/UserSelector';
import { getUserList, getUserViewList } from '../../store/Slice/User/UserSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  deleteRowApi,
  getAccessLevelData,
  getStateData,
  getDepartmentList
} from '../../store/Slice/Global/GlobalSlice';
import { UserApi } from '../../utils/Apis';
import useDebounce from '../../utils/useDebounce';
import RevenueService from '../services/RevenueService';
import PartnerHierarchyPage from '../OnBoarding/PartnerHierarchyComponent';
import { pillTabsSx } from '../Global/Styling';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'USER ID', sortable: true },
  { id: 'name', label: 'USER NAME', sortable: true },
  { id: 'users', label: 'USER EMAIL', sortable: true },
  { id: 'category', label: 'DEPARTMENT', sortable: true },
  { id: 'roleLavel', label: 'ACCESS LEVEL', sortable: true },
  { id: 'created', label: 'LAST UPDATE', sortable: true },
  { id: 'active', label: 'STATUS', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query = '') {
  const list = Array.isArray(array) ? array : [];
  const stabilizedThis = list.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0] || {}, b[0] || {});
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (q) {
    return list.filter((_user) => {
      const name = (_user && (_user.name ?? _user.userName ?? _user.user_name ?? '')) || '';
      return String(name).toLowerCase().includes(q);
    });
  }

  return stabilizedThis.map((el) => el[0]);
}

export default function Users() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [typeIdValue, setTypeIdValue] = useState(304);
  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [selectedDeleteUserId, setSelectedDeleteUserId] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);

  const [filterState, setFilterState] = useState({
    product: ' ',
    productId: null,
    productArray: [],
    city: null,
    cityId: null,
    cityArray: [],
    isAccordionItemSelected: '',
    isActive: '',
    statusId: null,
    statusArray: [],
    employmentType: ' ',
    employmentTypeId: null,
    employmentTypeArray: [],
    departmentId: null,
    branchId: null,
    accessLevelId: null
  });

  const dispatch = useDispatch();
  const { UserListData } = useUserSelector();
  const { LoginData } = useLoginSelector();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(filterName, 600);
  const { DownloadUserRecord } = RevenueService;

  const getStateList = useCallback(() => {
    const data = {
      countryId: 101
    };
    dispatch(getStateData(data));
  }, [dispatch]);

  useEffect(() => {
    getStateList();
  }, [getStateList]);

  const handledeleteUserRow = (userId) => {
    setSelectedDeleteUserId(userId);
    setOpenDeleteUser(true);
  };

  const deleteUserRow = useCallback(
    async (userIdToDelete) => {
      if (!userIdToDelete) return;
      const credentials = {
        userId: userIdToDelete,
        loggedInUserId: LoginData?.userId
      };
      const endpoint = `${UserApi?.deleteUser}`;
      const result = await dispatch(deleteRowApi(endpoint, credentials));
      setOpenDeleteUser(false);
      if (result?.status === 'success') {
        navigate('/users/');
        getUserListApi();
      }
    },
    [dispatch, navigate, LoginData?.userId]
  );

  const viewUserRow = useCallback(
    async ({ userId, action }) => {
      const endpoint = `${UserApi?.viewUser}/${userId}?loggedInUserId=${LoginData?.userId}`;
      const result = await dispatch(getUserViewList(endpoint));

      if (result && action === 'view') {
        navigate(`/users/view/${userId}`);
      } else if (result && action === 'edit') {
        navigate(`/users/edit/${userId}`);
      }
    },
    [dispatch, navigate, LoginData?.userId]
  );

  const handleRequestSort = (property) => {
    if (property !== 'action') {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = (UserListData || []).map((n) => n.userId);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const filteredUsers = useMemo(
    () => applySortFilter(UserListData, getComparator(order, orderBy), filterName),
    [UserListData, order, orderBy, filterName]
  );

  const isUserNotFound = filteredUsers.length === 0;

  const handleExport = async (type) => {
    if (selected.length > 0) {
      type = null;
    }
    const credential = {
      userIds: selected,
      typeId: type,
      loggedInUserId: LoginData?.userId
    };

    try {
      const response = await DownloadUserRecord(credential, LoginData?.jwt);
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();
      const fileName = `userDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.csv`;
      const mimeType =
        contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const currentTableHead =
    typeIdValue === 302 ? TABLE_HEAD.filter((h) => h.label !== 'ACTION') : TABLE_HEAD;

  const getUserListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      loggedInUserId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: 58,
      filterModel: {
        departmentId: filterState?.departmentId ?? null,
        branchId: filterState?.branchId ?? null,
        accessLevelId: filterState?.accessLevelId ?? null,
        isActive: filterState?.isActive ?? null,
        typeId: typeIdValue
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : '',
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      }
    };
    dispatch(getUserList(credentials));
  }, [dispatch, typeIdValue, LoginData, debouncedSearch, order, filterState, rowsPerPage, page]);

  useEffect(() => {
    getUserListApi();
  }, [getUserListApi]);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchAccessLevel = useCallback(() => {
    dispatch(getAccessLevelData(LoginData?.companyId));
  }, [dispatch, LoginData]);

  useEffect(() => {
    fetchAccessLevel();
  }, [fetchAccessLevel]);

  useEffect(() => {
    if (!UserListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(UserListData.length >= rowsPerPage);
  }, [UserListData, rowsPerPage]);

  const getDepartmentAPi = useCallback(() => {
    if (!LoginData) return;
    const credential = {
      companyId: LoginData?.companyId
    };
    dispatch(getDepartmentList(credential));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getDepartmentAPi();
  }, [getDepartmentAPi]);

  useEffect(() => {
    setPage(0);
  }, [filterName, rowsPerPage, debouncedSearch, filterState, typeIdValue, tabValue]);

  const totalCount = Array.isArray(UserListData) ? UserListData.length : 0;
  const startRecord = totalCount > 0 ? page * rowsPerPage + 1 : 0;
  const endRecord = totalCount > 0 ? page * rowsPerPage + totalCount : 0;

  return (
    <Page title="Partner Management System || Users">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <PeopleAltOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Users
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Manage company users, department assignments, and access permissions.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Tabs: Employee & Hierarchy */}
        <Tabs value={tabValue} onChange={handleChange} sx={{ ...pillTabsSx, mb: 2.5 }}>
          <Tab
            icon={<BadgeIcon />}
            iconPosition="start"
            label="Employee"
            {...a11yProps(0)}
            onClick={() => setTypeIdValue(304)}
          />
          <Tab
            icon={<AccountTreeIcon />}
            iconPosition="start"
            label="Hierarchy"
            {...a11yProps(1)}
            disabled={selected?.length !== 1}
          />
        </Tabs>

        {/* Tab 0: Employee List */}
        <TabPanel value={tabValue} index={0}>
          <Card
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              overflow: 'hidden',
              bgcolor: '#ffffff'
            }}
          >
            <UserListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              parent="users"
              filterState={filterState}
              setFilterState={setFilterState}
              typeIdValue={typeIdValue}
              handleExport={handleExport}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 900, minHeight: 380 }}>
                <Table>
                  <thead>
                    <TableRow>
                      <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                        <Checkbox
                          indeterminate={
                            selected.length > 0 && selected.length < (UserListData?.length || 0)
                          }
                          checked={
                            UserListData?.length > 0 && selected.length === UserListData?.length
                          }
                          onChange={handleSelectAllClick}
                          sx={{
                            color: '#cbd5e1',
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                          }}
                        />
                      </StyledHeadCell>
                      {currentTableHead.map((headCell) => (
                        <StyledHeadCell
                          key={headCell.id}
                          onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                          sx={{
                            cursor: headCell.sortable ? 'pointer' : 'default',
                            userSelect: 'none',
                            ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                            spacing={0.5}
                          >
                            <span>{headCell.label}</span>
                            {headCell.sortable && (
                              <SwapVertIcon
                                sx={{
                                  fontSize: 16,
                                  color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                                }}
                              />
                            )}
                          </Stack>
                        </StyledHeadCell>
                      ))}
                    </TableRow>
                  </thead>
                  <TableBody>
                    {filteredUsers.map((row) => {
                      const {
                        userId,
                        userName,
                        email,
                        departmentName,
                        accessLevel,
                        createdOn,
                        active
                      } = row;
                      const isItemSelected = selected.includes(userId);

                      return (
                        <TableRow
                          hover
                          key={userId}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:hover': { backgroundColor: '#f8fafc !important' },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: 'rgba(2, 132, 199, 0.06)'
                            }
                          }}
                        >
                          <StyledTableCell padding="checkbox" sx={{ pl: 2.5 }}>
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, userId)}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#0284c7' }
                              }}
                            />
                          </StyledTableCell>

                          <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                            {userId}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {userName}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {email || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {departmentName || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {accessLevel || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {createdOn ? fDateNew(createdOn) : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '20px',
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor:
                                  active === true || active === 'Yes' ? '#dcfce7' : '#fee2e2',
                                color: active === true || active === 'Yes' ? '#16a34a' : '#dc2626'
                              }}
                            >
                              {active === true || active === 'Yes' ? 'Active' : 'Inactive'}
                            </Box>
                          </StyledTableCell>

                          <StyledTableCell align="center" width={80}>
                            <UserMoreMenu
                              rowId={userId}
                              parent="users"
                              deleteUserRow={handledeleteUserRow}
                              viewUserRow={viewUserRow}
                            />
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell
                          align="center"
                          colSpan={currentTableHead.length + 1}
                          sx={{ py: 6 }}
                        >
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            {/* Pagination Footer */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderTop: '1px solid #f1f5f9',
                gap: 2
              }}
            >
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                Showing{' '}
                <span style={{ fontWeight: 600, color: '#334155' }}>
                  {startRecord} to {endRecord}
                </span>{' '}
                records
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>Rows per page</Typography>
                  <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    sx={{
                      height: 32,
                      fontSize: 13,
                      borderRadius: '6px',
                      '& fieldset': { borderColor: '#e2e8f0' }
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <IconButton
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    sx={{
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      width: 32,
                      height: 32,
                      color: '#64748b',
                      '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '6px',
                      bgcolor: '#0284c7',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                      px: 1
                    }}
                  >
                    {page + 1}
                  </Box>

                  <IconButton
                    size="small"
                    disabled={totalCount < rowsPerPage}
                    onClick={() => setPage((prev) => prev + 1)}
                    sx={{
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      width: 32,
                      height: 32,
                      color: '#64748b',
                      '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </TabPanel>

        {/* Tab 1: Hierarchy */}
        <TabPanel value={tabValue} index={1}>
          <PartnerHierarchyPage partnerId={selected[0]} />
        </TabPanel>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteUser}
          onClose={() => setOpenDeleteUser(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#dc2626'
                }}
              >
                <WarningAmberIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Delete User
              </Typography>
            </Stack>
            <IconButton
              onClick={() => setOpenDeleteUser(false)}
              size="small"
              sx={{
                color: '#64748b',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Typography sx={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
              Are you sure you want to delete this user? This action cannot be undone.
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              gap: 1
            }}
          >
            <Button
              onClick={() => setOpenDeleteUser(false)}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                px: 2.5,
                py: 0.8,
                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => deleteUserRow(selectedDeleteUserId)}
              variant="contained"
              sx={{
                borderRadius: '8px',
                bgcolor: '#dc2626',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                px: 2.5,
                py: 0.8,
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
                '&:hover': { bgcolor: '#b91c1c' }
              }}
            >
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}
